import type { Dictionary, DictValue } from './dictionaries/types'
import { tr } from './dictionaries/tr'
import { en } from './dictionaries/en'
import { fetchAdminUiDictionary } from '@/api/admin-ui-translations'

export type { Dictionary }

const localDictionaries: Record<string, Dictionary> = { tr, en }

export const ADMIN_LOCALE_KEY = 'admin_locale'

export function deepMergeDict(base: DictValue, overlay: DictValue): DictValue {
  if (typeof overlay === 'string') return overlay
  if (typeof base === 'string' || base == null) return overlay
  if (!overlay || typeof overlay !== 'object') return base

  const out: { [key: string]: DictValue } = { ...(base as object) }
  for (const [k, v] of Object.entries(overlay)) {
    if (v == null) continue
    const prev = out[k]
    out[k] =
      typeof v === 'string' || typeof prev === 'string' || prev == null
        ? v
        : deepMergeDict(prev, v)
  }
  return out
}

export function getLocalDictionary(locale: string): Dictionary {
  return localDictionaries[locale] ?? localDictionaries.en ?? localDictionaries.tr
}

/** Resolve admin locale: localStorage → supported → tr */
export function resolveAdminLocale(supported: string[]): string {
  const stored =
    typeof window !== 'undefined' ? localStorage.getItem(ADMIN_LOCALE_KEY) : null
  if (stored && supported.includes(stored)) return stored
  if (supported.includes('tr')) return 'tr'
  return supported[0] ?? 'tr'
}

export function setAdminLocale(code: string): void {
  localStorage.setItem(ADMIN_LOCALE_KEY, code)
}

/**
 * Local fallback + API merge. API wins on overlap.
 * Fallback chain for missing locales: requested → en → tr
 */
export async function getAdminDictionary(locale: string): Promise<Dictionary> {
  const local = getLocalDictionary(locale)
  const enLocal = getLocalDictionary('en')
  const trLocal = getLocalDictionary('tr')

  let merged = deepMergeDict(
    trLocal as unknown as DictValue,
    deepMergeDict(enLocal as unknown as DictValue, local as unknown as DictValue),
  ) as Dictionary

  try {
    const remote = await fetchAdminUiDictionary(locale)
    if (remote && typeof remote === 'object') {
      merged = deepMergeDict(merged as unknown as DictValue, remote as DictValue) as Dictionary
    }
  } catch {
    /* keep local */
  }

  return merged
}

/** Dot-path lookup: "nav.home" → dictionary.nav.home */
export function translate(
  dictionary: Dictionary,
  key: string,
  params?: Record<string, string | number>,
): string {
  const parts = key.split('.')
  let cur: DictValue = dictionary as unknown as DictValue
  for (const part of parts) {
    if (cur == null || typeof cur === 'string') return key
    cur = cur[part]
  }
  if (typeof cur !== 'string') return key
  if (!params) return cur
  return Object.entries(params).reduce(
    (text, [k, v]) => text.replaceAll(`{${k}}`, String(v)),
    cur,
  )
}
